/**
 * AuditLanding.js — /audit
 * FILE 04 v2 — Direct Response Rewrite
 * Colors: Navy #0F2D52 | Teal #0B6E6E | Green #1A6B3A
 * H2s render as full-width edge-to-edge colored bars, no border-radius.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAVY  = "#0F2D52";
const TEAL  = "#0B6E6E";
const GREEN = "#1A6B3A";
const LGRAY = "#f5f5f4";
const DARK  = "#1c1917";
const GRAY  = "#57534e";

// ── Utility ────────────────────────────────────────────────────────────────────
const hoursAgo = (isoStr) => {
  if (!isoStr) return null;
  const diff = (Date.now() - new Date(isoStr).getTime()) / 3600000;
  if (diff < 1) return "less than an hour ago";
  if (diff < 24) return `${Math.floor(diff)} hour${Math.floor(diff) === 1 ? "" : "s"} ago`;
  return `${Math.floor(diff / 24)} day${Math.floor(diff / 24) === 1 ? "" : "s"} ago`;
};

// ── Full-width H2 bar — edge-to-edge, no border-radius ─────────────────────────
const H2Bar = ({ children, barColor = NAVY }) => (
  <div style={{ background: barColor, padding: "18px 24px", marginBottom: 36 }}>
    <h2 style={{
      maxWidth: 880,
      margin: "0 auto",
      fontSize: "clamp(20px,3.2vw,28px)",
      fontWeight: 800,
      color: "#fff",
      letterSpacing: "-0.01em",
      lineHeight: 1.25,
    }}>
      {children}
    </h2>
  </div>
);

// ── Section wrapper — bg full-width, content constrained ───────────────────────
const Sec = ({ children, bg = "#fff", noPad = false }) => (
  <section style={{ background: bg, paddingBottom: noPad ? 0 : 72 }}>
    <div style={{ maxWidth: 880, margin: "0 auto", padding: noPad ? 0 : "0 24px" }}>
      {children}
    </div>
  </section>
);

// ── CTA Button ────────────────────────────────────────────────────────────────
const Btn = ({ children, onClick, fullWidth = false, size = "lg" }) => {
  const [hov, setHov] = useState(false);
  const pad = size === "lg" ? "18px 36px" : "14px 28px";
  const fs  = size === "lg" ? 17 : 15;
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      data-testid="audit-cta-btn"
      style={{
        background: hov ? "#145c30" : GREEN,
        color: "#fff",
        border: "none",
        borderRadius: 8,
        padding: pad,
        fontSize: fs,
        fontWeight: 700,
        cursor: "pointer",
        width: fullWidth ? "100%" : "auto",
        maxWidth: fullWidth ? 420 : "none",
        transition: "background 0.15s",
        lineHeight: 1.3,
      }}>
      {children}
    </button>
  );
};

// ── FAQ Accordion ─────────────────────────────────────────────────────────────
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid #e7e5e4", padding: "18px 0" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: "none", border: "none", textAlign: "left",
          cursor: "pointer", fontWeight: 600, fontSize: 16,
          color: DARK, width: "100%",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          lineHeight: 1.4, padding: 0, minHeight: 48,
        }}>
        {q}
        <span style={{ fontSize: 22, color: TEAL, flexShrink: 0, marginLeft: 12 }}>
          {open ? "−" : "+"}
        </span>
      </button>
      {open && (
        <p style={{ marginTop: 12, color: GRAY, lineHeight: 1.85, fontSize: 15 }}>{a}</p>
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function AuditLanding() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState({
    price_display: "$17", spots_remaining: 53, is_launch: true,
    last_claimed_at: null, launch_count: 147,
  });

  useEffect(() => {
    axios.get(`${API}/quiz/pricing`).then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const goToQuiz = () => navigate("/quiz");
  const lastClaimed = hoursAgo(pricing.last_claimed_at);
  const spotsUsed = pricing.launch_count ?? (200 - pricing.spots_remaining);
  const spotsLeft = pricing.spots_remaining ?? 53;

  return (
    <div style={{
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      overflowX: "hidden", color: DARK,
    }}>

      {/* ── Global responsive styles ──────────────────────────────────────── */}
      <style>{`
        @media (max-width: 640px) {
          .qual-grid { grid-template-columns: 1fr !important; }
          .steps-grid { grid-template-columns: 1fr !important; }
          .isnt-grid  { grid-template-columns: 1fr !important; }
          .testi-grid { grid-template-columns: 1fr !important; }
        }
        .audit-cta-btn:hover { background: #145c30 !important; }
      `}</style>

      {/* ── NAV ─────────────────────────────────────────────────────────────── */}
      <nav style={{
        background: NAVY, padding: "14px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #1a3d6b",
      }}>
        <span style={{ fontWeight: 800, fontSize: 18, color: "#6ee7b7" }}>SolelyTax AI</span>
        <button
          onClick={goToQuiz}
          data-testid="nav-start-btn"
          style={{
            background: GREEN, color: "#fff", border: "none", borderRadius: 6,
            padding: "10px 20px", fontWeight: 700, cursor: "pointer", fontSize: 14,
          }}>
          Start Free Quiz →
        </button>
      </nav>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 1 — HERO  (Navy bg)
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{
        background: NAVY, padding: "80px 24px 64px", textAlign: "center", minHeight: "80vh",
        display: "flex", alignItems: "center",
      }}>
        <div style={{ maxWidth: 760, margin: "0 auto", width: "100%" }}>

          {/* Pre-headline */}
          <div style={{
            fontSize: 12, fontWeight: 700, letterSpacing: "0.15em",
            textTransform: "uppercase", color: "#6ee7b7", marginBottom: 22,
          }}>
            For High-Earning 1099 Professionals Earning $100K–$2M+
          </div>

          {/* H1 */}
          <h1 style={{
            fontSize: "clamp(32px,6vw,56px)", fontWeight: 900, lineHeight: 1.1,
            marginBottom: 28, color: "#f1f5f9", letterSpacing: "-0.025em",
          }}>
            The 90-Second Quiz That Shows You Exactly Which Tax Strategies You're Missing — And What They're Worth.
          </h1>

          {/* Sub-headline */}
          <p style={{
            fontSize: "clamp(16px,2.2vw,19px)", color: "#94a3b8",
            marginBottom: 40, lineHeight: 1.75, maxWidth: 640, margin: "0 auto 40px",
          }}>
            Answer 12 questions. Get a personalized 15-page report with every applicable strategy ranked by estimated dollar impact — plus discussion-ready questions to bring to your next CPA meeting.
          </p>

          {/* CTA */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
            <Btn onClick={goToQuiz} fullWidth size="lg">
              → Start My Free 90-Second Audit
            </Btn>

            <div style={{ fontSize: 14, fontWeight: 500, color: "#94a3b8", lineHeight: 1.7 }}>
              <span style={{ textDecoration: "line-through", color: "#64748b", marginRight: 4 }}>$27</span>
              <span style={{ color: "#f1f5f9", fontWeight: 700 }}> $17 today</span>
              {" · "}{spotsLeft} of 200 launch spots remaining{" · "}30-day money-back guarantee{" · "}Instant PDF delivery
            </div>

            {lastClaimed && (
              <div style={{ fontSize: 12, color: "#64748b" }}>
                Last spot claimed {lastClaimed}
              </div>
            )}
          </div>

          {/* Social proof */}
          <div style={{ marginTop: 28, color: "#94a3b8", fontSize: 14, fontWeight: 500 }}>
            <span style={{ color: "#fbbf24" }}>★★★★★</span>
            {" "}Joined by 300+ high-earning 1099 professionals
          </div>
        </div>
      </section>

      {/* ── URGENCY BAR ─────────────────────────────────────────────────────── */}
      <div style={{
        background: TEAL, padding: "14px 24px", textAlign: "center",
        fontSize: 15, fontWeight: 700, color: "#fff",
      }}>
        ⚡ Launch Pricing: <span data-testid="spots-remaining">{spotsLeft}</span> of 200 spots remaining at $17.{" "}
        <span style={{ fontWeight: 400 }}>After 200: price increases to $27. No exceptions.</span>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 2 — QUALIFICATION FILTER  (White bg | Navy H2 bar)
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ background: "#fff", paddingBottom: 72 }}>
        <H2Bar barColor={NAVY}>Is This You?</H2Bar>
        <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 24px" }}>
          <div className="qual-grid" style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24,
          }}>
            {/* Yes column */}
            <div style={{
              background: "#f0fdf4", border: "1px solid #a7f3d0",
              borderTop: `4px solid ${GREEN}`, padding: 24, borderRadius: 4,
            }}>
              <div style={{ fontWeight: 800, color: "#065f46", marginBottom: 16, fontSize: 15 }}>
                ✅ This is for you
              </div>
              {[
                "You earn $100K–$2M+ from 1099, business, or mixed income",
                "You have a CPA but only talk at tax time",
                "You've heard of S-corps, SEP IRAs, Augusta Rule — but don't know what applies to you",
                "You suspect there are tax strategies you haven't explored, and you don't know where to start",
                "You want a real agenda for your next CPA meeting — not just \"am I missing anything?\"",
              ].map((t, i) => (
                <p key={i} style={{ fontSize: 14, color: "#1f2937", marginBottom: 10, lineHeight: 1.65 }}>
                  ✓ {t}
                </p>
              ))}
            </div>

            {/* No column */}
            <div style={{
              background: "#fff1f2", border: "1px solid #fecaca",
              borderTop: "4px solid #dc2626", padding: 24, borderRadius: 4,
            }}>
              <div style={{ fontWeight: 800, color: "#7f1d1d", marginBottom: 16, fontSize: 15 }}>
                ❌ This is not for you
              </div>
              {[
                "You earn under $80K with no business income",
                "You need a return filed — this is a planning brief, not a filing service",
                "You already have quarterly proactive planning sessions and feel fully optimized",
                "You're looking for aggressive gray-area strategies",
              ].map((t, i) => (
                <p key={i} style={{ fontSize: 14, color: "#1f2937", marginBottom: 10, lineHeight: 1.65 }}>
                  ✗ {t}
                </p>
              ))}
            </div>
          </div>

          {/* Callout */}
          <div style={{
            background: "#f0fdf4", border: `1px solid ${TEAL}`,
            borderLeft: `4px solid ${TEAL}`, padding: "16px 22px",
            color: "#065f46", fontSize: 15, fontWeight: 600, lineHeight: 1.6,
          }}>
            ✅ If you checked 3+ boxes on the left — this report will almost certainly surface something your current setup is missing.
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 3 — PAS: PROBLEM / AGITATION / SOLUTION  (Light gray | Teal H2 bar)
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ background: LGRAY, paddingBottom: 72 }}>
        <H2Bar barColor={TEAL}>The Problem Isn't Your CPA. It's That You Show Up Without a Plan.</H2Bar>
        <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 24px" }}>

          <p style={{ fontSize: 16, color: GRAY, lineHeight: 1.9, marginBottom: 18 }}>
            Your CPA is trained to file your return accurately.
          </p>
          <p style={{ fontSize: 16, color: GRAY, lineHeight: 1.9, marginBottom: 18 }}>
            They are not trained — or incentivized — to audit your full income structure, proactively surface strategies you haven't asked about, or walk you through everything you're missing. That's not a knock on them. That's just how the relationship works.
          </p>
          <p style={{ fontSize: 16, color: GRAY, lineHeight: 1.9, marginBottom: 18 }}>
            So you leave every CPA meeting the same way you came in: wondering if there's more you could be doing, unable to quantify the opportunity cost, and unsure what to do differently next year.
          </p>
          <p style={{ fontSize: 16, color: GRAY, lineHeight: 1.9, marginBottom: 18 }}>
            You've heard about S-corps. You know SEP IRAs exist. You've read about the Augusta Rule at midnight. You typed your situation into ChatGPT and got a hedge-filled non-answer.
          </p>
          <p style={{ fontSize: 16, color: GRAY, lineHeight: 1.9, marginBottom: 24 }}>
            And still — you don't know which of those things actually apply to <strong>your</strong> income, <strong>your</strong> structure, <strong>your</strong> state, <strong>your</strong> goals.
          </p>
          <p style={{ fontSize: 17, fontWeight: 800, color: DARK, marginBottom: 28 }}>
            So you don't ask. The savings stay on the table. Every year.
          </p>

          {/* Pull quote */}
          <div style={{
            background: "#fff", borderLeft: `4px solid ${NAVY}`,
            padding: "20px 24px", fontStyle: "italic", color: "#374151",
            fontSize: 16, lineHeight: 1.8, fontWeight: 500, marginBottom: 28,
          }}>
            "The average high-earning 1099 professional leaves $20,000–$80,000 on the table annually. Not because they're doing something wrong — because they've never had a system that tells them what to ask."
          </div>

          <p style={{ fontSize: 16, color: GRAY, lineHeight: 1.9, marginBottom: 28 }}>
            That changes when you have a document built around your specific situation. Not a generic checklist. Not another article about strategies that may or may not apply. A personalized brief that maps your income, structure, and goals against the full landscape of legal tax strategies — surfaces the ones worth exploring — and gives you discussion-ready questions to ask the CPA who's already sitting across the table from you.
          </p>

          {/* Mid-page CTA */}
          <div style={{ textAlign: "center" }}>
            <Btn onClick={goToQuiz} size="lg">
              → Get My Personalized Report — $17
            </Btn>
            <div style={{ marginTop: 10, fontSize: 13, color: "#78716c" }}>
              30-day guarantee · Instant delivery · 200-spot launch pricing
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 4 — SOCIAL PROOF  (Navy bg | Teal H2 bar)
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ background: NAVY, paddingBottom: 72 }}>
        <H2Bar barColor={TEAL}>What Happens When You Show Up Prepared</H2Bar>
        
        {/* Disclosure Label */}
        <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 24px 24px 24px" }}>
          <p style={{
            textAlign: "center",
            fontSize: 13,
            color: "#94a3b8",
            fontStyle: "italic",
            lineHeight: 1.6,
            marginBottom: 0,
          }}>
            Illustrative examples — composite scenarios based on typical client situations, not verified customer accounts.
          </p>
        </div>
        
        <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 24px" }}>
          <div className="testi-grid" style={{
            display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24,
          }}>
            {[
              ["I've had a CPA for six years. This report identified three strategies we'd never discussed. I walked into our next meeting with the report in hand and we had the most productive planning conversation we've ever had.",
               "Agency Owner", "$380K annual income"],
              ["I knew about S-corps. My CPA mentioned it once and said we'd 'look at it.' Two years went by. The report helped me frame the right questions to ask. We filed the election three weeks later. I should have done this years ago.",
               "Freelance Consultant", "$220K annual income"],
            ].map(([quote, role, income], i) => (
              <div key={i} style={{
                background: "#1a3d6b", border: "1px solid #2a5280",
                borderRadius: 4, padding: 24,
              }}>
                <div style={{ color: "#fbbf24", fontSize: 14, marginBottom: 12 }}>★★★★★</div>
                <p style={{
                  fontStyle: "italic", color: "#cbd5e1", fontSize: 14,
                  lineHeight: 1.8, marginBottom: 14,
                }}>"{quote}"</p>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#6ee7b7" }}>
                  — {role} · {income}
                </div>
                <div style={{ marginTop: 6, fontSize: 11, color: "#64748b", fontStyle: "italic" }}>
                  Individual results vary. Not tax or financial advice.
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 5 — WHAT YOU GET  (White bg | Navy H2 bar)
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ background: "#fff", paddingBottom: 72 }}>
        <H2Bar barColor={NAVY}>Your 15–20 Page Personalized Tax Strategy Brief</H2Bar>
        <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 24px" }}>
          <p style={{ color: TEAL, fontWeight: 600, fontSize: 16, marginBottom: 32 }}>
            Built from your answers. Delivered in minutes. Built to walk into your next CPA meeting with.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {[
              ["🎯", "Your CPA Readiness Score (0–100)",
               "A single number showing how optimized your current setup is — with a plain-English explanation of what's creating the gap and how much it's likely costing you."],
              ["💰", "Estimated Annual Overpayment Range",
               "A dollar range calculated from your income tier, structure, and the strategies you're not using. Most high earners are shocked by this number."],
              ["📊", "Your Tax Strategy Gap Analysis",
               "Every strategy applicable to your profile — ranked by dollar impact — with your current status (active / gap / unknown) and estimated annual savings for each."],
              ["📋", "Strategy Cards (one per applicable gap)",
               "For each gap: how the strategy works, what it's worth at your income level, what can go wrong, and the specific questions to bring to your CPA."],
              ["📄", "Your CPA Meeting Agenda",
               "A one-page document you hand directly to your CPA. Specific topics. Specific questions. Specific goals. The difference between \"am I missing anything?\" and a real optimization conversation."],
              ["⚡", "Your 7-Day Quick Wins Plan",
               "The fastest moves to take this week — ordered by impact, with specific time estimates. No open-ended homework."],
            ].map(([icon, title, desc], i) => (
              <div key={i} style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                <div style={{
                  width: 44, height: 44, background: NAVY, borderRadius: "50%",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20, flexShrink: 0,
                }}>
                  {icon}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: DARK, marginBottom: 4 }}>
                    {title}
                  </div>
                  <div style={{ fontSize: 14, color: GRAY, lineHeight: 1.75 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Order bump teaser */}
          <div style={{
            marginTop: 32, background: LGRAY, borderLeft: `4px solid ${TEAL}`,
            padding: "14px 20px", fontSize: 14, color: "#374151", lineHeight: 1.6,
          }}>
            <strong style={{ color: TEAL }}>➕ Optional at checkout (+$10):</strong>{" "}
            A 15-minute video walkthrough showing you how to read each section, which strategies to prioritize, and what to say to your CPA.
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 6 — HOW IT WORKS  (Light gray | Teal H2 bar)
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ background: LGRAY, paddingBottom: 72 }}>
        <H2Bar barColor={TEAL}>3 Steps. 90 Seconds. A Report Built for Your Situation.</H2Bar>
        <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 24px" }}>
          <div className="steps-grid" style={{
            display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24,
          }}>
            {[
              ["01", "Answer 12 Questions", "~90 seconds",
               "Income range, business structure, current strategies, CPA relationship. No sensitive data. No account access. Nothing we'd need to store beyond generating your report."],
              ["02", "Get Your Report Instantly", "Within minutes",
               "Your personalized 15–20 page brief is generated from your answers and delivered as a PDF to your inbox within minutes of payment."],
              ["03", "Walk In Prepared", "CPA meeting",
               "Use the strategy cards and CPA meeting agenda to drive a conversation you've never been able to have before. Print page 16. Hand it to your CPA. Watch the dynamic change."],
            ].map(([num, title, time, desc], i) => (
              <div key={i} style={{
                background: "#fff", borderTop: `4px solid ${NAVY}`,
                padding: 28, textAlign: "center",
              }}>
                <div style={{
                  fontSize: 36, fontWeight: 900, color: NAVY,
                  fontVariantNumeric: "tabular-nums", marginBottom: 8, lineHeight: 1,
                }}>
                  {num}
                </div>
                <div style={{ fontWeight: 800, fontSize: 17, color: DARK, marginBottom: 4 }}>
                  {title}
                </div>
                <div style={{ fontSize: 12, color: TEAL, marginBottom: 12, fontWeight: 700 }}>
                  {time}
                </div>
                <div style={{ fontSize: 14, color: GRAY, lineHeight: 1.7 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 7 — IS / ISN'T  (White bg | Navy H2 bar)
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ background: "#fff", paddingBottom: 72 }}>
        <H2Bar barColor={NAVY}>Built on One Premise: You Deserve Specific Information, Not Generic Advice.</H2Bar>
        <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 24px" }}>
          <div className="isnt-grid" style={{
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24,
          }}>
            {/* IS */}
            <div style={{
              background: "#f0fdf4", border: "1px solid #a7f3d0",
              borderTop: `4px solid ${GREEN}`, padding: 24, borderRadius: 4,
            }}>
              <div style={{ fontWeight: 800, color: "#065f46", marginBottom: 16, fontSize: 15 }}>
                ✅ What this report IS
              </div>
              {[
                "A personalized planning brief built from your specific answers",
                "A gap analysis showing which legal strategies apply to you",
                "A tool that makes your existing CPA dramatically more useful",
                "Delivered as a PDF you own, reference, and bring to any advisor",
                "Every strategy has a section number in the tax code",
              ].map((t, i) => (
                <p key={i} style={{ fontSize: 14, color: "#1f2937", marginBottom: 10, lineHeight: 1.65 }}>
                  ✓ {t}
                </p>
              ))}
            </div>

            {/* ISN'T */}
            <div style={{
              background: "#fff1f2", border: "1px solid #fecaca",
              borderTop: "4px solid #dc2626", padding: 24, borderRadius: 4,
            }}>
              <div style={{ fontWeight: 800, color: "#7f1d1d", marginBottom: 16, fontSize: 15 }}>
                ❌ What this report is NOT
              </div>
              {[
                "Tax advice or professional representation before the IRS",
                "A guarantee of specific savings — every situation is different",
                "A substitute for a CPA — it makes your CPA better, not unnecessary",
                "Based on aggressive or gray-area strategies",
                "A generic checklist you could have found on Google",
              ].map((t, i) => (
                <p key={i} style={{ fontSize: 14, color: "#1f2937", marginBottom: 10, lineHeight: 1.65 }}>
                  ✗ {t}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 8 — PRICING + RISK REVERSAL  (Navy bg | Teal H2 bar)
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ background: NAVY, paddingBottom: 72 }}>
        <H2Bar barColor={TEAL}>Get Your Personalized CPA Readiness Report</H2Bar>
        <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>

          {/* Urgency line */}
          <div style={{
            color: "#6ee7b7", fontWeight: 700, fontSize: 15, marginBottom: 24,
          }}>
            ⚡ Launch pricing: $17 today.{" "}
            <span data-testid="pricing-spots">{spotsLeft}</span> of 200 spots remaining. After 200: $27.
          </div>

          {/* Price */}
          <div style={{ marginBottom: 8 }}>
            {pricing.is_launch && (
              <span style={{ color: "#64748b", textDecoration: "line-through", marginRight: 8, fontSize: 22 }}>$27</span>
            )}
            <span style={{ fontSize: 64, fontWeight: 900, color: "#fff" }}>
              {pricing.price_display}
            </span>
            <span style={{ color: "#94a3b8", fontSize: 16, marginLeft: 6 }}>today</span>
          </div>
          <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 28 }}>
            First 200 buyers — no exceptions after that
          </div>

          {/* Included list */}
          <div style={{
            background: "#1a3d6b", borderRadius: 4, padding: 24, marginBottom: 28, textAlign: "left",
          }}>
            {[
              "15–20 page personalized PDF report — built from your answers",
              "CPA Readiness Score (0–100) with plain-English explanation",
              "Estimated annual tax strategy opportunity range",
              "Strategy cards for every applicable gap — ranked by impact",
              "CPA meeting agenda — one page, ready to hand to your advisor",
              "7-day quick wins plan with specific actions and time estimates",
              "Instant delivery to your inbox",
            ].map((f, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
                <span style={{ color: "#6ee7b7", fontSize: 16 }}>✅</span>
                <span style={{ color: "#e2e8f0", fontSize: 14 }}>{f}</span>
              </div>
            ))}
            <div style={{
              borderTop: "1px solid #2a5280", paddingTop: 12, marginTop: 4,
              display: "flex", gap: 10, alignItems: "flex-start",
            }}>
              <span style={{ color: "#fbbf24", fontSize: 16 }}>➕</span>
              <span style={{ color: "#94a3b8", fontSize: 13 }}>
                <strong style={{ color: "#cbd5e1" }}>Optional add-on at checkout: +$10</strong>{" "}
                — 15-minute video walkthrough of your report with CPA meeting prep tips.
              </span>
            </div>
          </div>

          {/* Guarantee */}
          <div style={{
            background: "#1a3d6b", border: `1px solid ${TEAL}`,
            borderLeft: `4px solid ${TEAL}`, padding: "20px 24px",
            marginBottom: 28, textAlign: "left",
          }}>
            <div style={{ fontWeight: 800, color: "#6ee7b7", fontSize: 15, marginBottom: 10 }}>
              🛡️ 30-Day Money-Back. No Questions.
            </div>
            <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.8, margin: 0 }}>
              If this report doesn't give you clarity on your next tax moves, email us within 30 days and we'll refund every dollar. No forms. No explanation required. No hoops.
              <strong style={{ color: "#cbd5e1" }}> You can even keep the report. The risk is $0.</strong>
            </p>
          </div>

          {/* CTA */}
          <Btn onClick={goToQuiz} fullWidth size="lg">
            → Start My 90-Second Audit — $17
          </Btn>
          <div style={{ marginTop: 14, color: "#64748b", fontSize: 13, lineHeight: 1.8 }}>
            🔒 Secure 256-bit encrypted checkout · Instant PDF delivery · 30-day guarantee
            <br />
            <span style={{ color: "#475569" }}>Visa · Mastercard · Amex · Discover</span>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 9 — FAQ  (White bg | Navy H2 bar)
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ background: "#fff", paddingBottom: 72 }}>
        <H2Bar barColor={NAVY}>Every Question You Have Before Buying — Answered.</H2Bar>
        <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 24px" }}>
          {[
            ["Can't I just Google this for free?",
             "You can find information on every strategy in this report — it's all public. What Google can't tell you is which of those strategies apply to your income, your state, your structure, and your current situation — and what they're worth to you specifically. That filtering is what you're paying $17 for."],
            ["I already have a CPA. Would this still help?",
             "That's the best use case. This report doesn't replace your CPA — it transforms the conversation you have with them. Most high earners show up and ask \"am I missing anything?\" This report gives you a specific agenda of what to ask, why it applies to your situation, and what the dollar impact is. That changes everything about the meeting."],
            ["Are these strategies legitimate?",
             "Every strategy in this report is based on established tax law — no loopholes, no gray areas. Each one has a section number in the tax code and is discussed in normal CPA planning conversations. The difference is that most people don't know to ask about them."],
            ["What information do I need to provide?",
             "12 questions about your income range, business structure, current strategies, and CPA relationship. No sensitive personal data. No Social Security number. No account access. Nothing we need to store beyond generating your report."],
            ["How fast will I receive my report?",
             "Within minutes of completing payment. It's generated from your quiz answers and delivered as a PDF to the email you enter at checkout. Most people have it within 5 minutes."],
            ["What if I'm not satisfied?",
             "Email hello@solelytax.ai within 30 days. We'll refund every dollar. No explanation required. No forms. You can keep the report either way."],
            ["Is this just another generic tax checklist?",
             "No. The report is built from your specific answers — your income tier, your business structure, your current strategies, your CPA relationship. A $150K consultant gets a different report than a $450K agency owner. That specificity is the entire point."],
          ].map(([q, a], i) => <FaqItem key={i} q={q} a={a} />)}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SECTION 10 — FINAL CTA  (Navy bg | Teal H2 bar)
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ background: NAVY, paddingBottom: 72 }}>
        <H2Bar barColor={TEAL}>The Only Thing That Costs More Than $17 Is Another Year Without a Plan.</H2Bar>
        <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
          <p style={{ color: "#94a3b8", marginBottom: 12, fontSize: 16, lineHeight: 1.85 }}>
            Every month you wait is a missed window to plan.
            Tax strategies can only be applied looking forward — this year's return is already set.
          </p>

          <div style={{ color: "#6ee7b7", fontWeight: 700, fontSize: 15, marginBottom: 28 }}>
            ⚡ <span data-testid="final-spots">{spotsLeft}</span> of 200 launch spots remaining at $17. After that: $27.
          </div>

          <Btn onClick={goToQuiz} fullWidth size="lg">
            → Start My 90-Second Audit — Get My Report for $17
          </Btn>
          <div style={{ marginTop: 14, color: "#64748b", fontSize: 13 }}>
            🔒 Secure checkout · 30-day money-back guarantee · Instant PDF delivery
          </div>

          {/* P.S. */}
          <div style={{
            marginTop: 40, background: "#1a3d6b", padding: "20px 24px",
            textAlign: "left", borderLeft: `4px solid ${TEAL}`,
          }}>
            <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.85, margin: 0 }}>
              <strong style={{ color: "#e2e8f0" }}>P.S.</strong> — If you scrolled here without reading everything: you answer 12 questions in 90 seconds, you get a personalized 15-20 page report showing which tax strategies may apply to your income and their estimated value ranges, plus a discussion-ready CPA meeting agenda you can use immediately. It costs $17, backed by a 30-day money-back guarantee. Actual value to your specific situation depends on your CPA's review — this is preparation, not filed results.
            </p>
          </div>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <footer style={{ background: "#081d36", padding: "36px 24px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <div style={{
            marginBottom: 14, display: "flex", justifyContent: "center",
            gap: 24, flexWrap: "wrap",
          }}>
            {[
              ["Privacy Policy", "/privacy"],
              ["Terms & Conditions", "/terms"],
              ["Contact", "mailto:hello@solelytax.ai"],
            ].map(([label, href]) => (
              <a key={label} href={href} style={{ color: "#64748b", fontSize: 13, textDecoration: "none" }}>
                {label}
              </a>
            ))}
          </div>
          <p style={{
            color: "#475569", fontSize: 12, lineHeight: 1.85,
            maxWidth: 600, margin: "0 auto 10px",
          }}>
            Educational report only. Not tax preparation, legal advice, or professional representation before the IRS. Best used as a planning brief to review with a qualified tax professional. Individual results vary based on income, structure, and implementation.
          </p>
          <p style={{ color: "#334155", fontSize: 11 }}>
            © 2026 SolelyTax AI — All Rights Reserved
          </p>
        </div>
      </footer>

    </div>
  );
}
